import React, { useState, useEffect } from "react";
import {
	Upload,
	FileText,
	CheckCircle2,
	XCircle,
	AlertCircle,
	AlertTriangle,
	Landmark,
	RefreshCw,
	FileSpreadsheet,
	Check,
} from "lucide-react";
import reqBoletoConfig from "../../../services/reqMultiTenant/reqBoletoConfig";
import reqBoleto from "../../../services/reqCobranca/reqBoleto";
import libFloat from "../../../util/libFloat";

const COR = {
	verde: "bg-emerald-100 text-emerald-800",
	azul: "bg-blue-100 text-blue-800",
	vermelho: "bg-red-100 text-red-800",
	ambar: "bg-amber-100 text-amber-800",
	cinza: "bg-gray-100 text-gray-700",
};

const BAIXADAS = ["BAIXADA", "BAIXADA_COM_JUROS", "BAIXADA_COM_DESCONTO"];

const RESULTADO_BAIXA = {
	BAIXADA: ["Parcela baixada", COR.verde],
	BAIXADA_COM_JUROS: ["Baixada com juros", COR.verde],
	BAIXADA_COM_DESCONTO: ["Baixada com desconto", COR.verde],
	JA_BAIXADA: ["Já estava baixada", COR.cinza],
	PARCELA_LIBERADA: ["Liberada para nova remessa", COR.azul],
	VALOR_DIVERGENTE: ["Valor divergente", COR.ambar],
	PARCELA_INDISPONIVEL: ["Parcela excluída ou inexistente", COR.ambar],
	REJEICAO_APOS_REGISTRO: ["Rejeição após registro", COR.ambar],
	ERRO: ["Erro ao baixar", COR.vermelho],
};

function precisaConferir(item) {
	if (["VALOR_DIVERGENTE", "PARCELA_INDISPONIVEL", "REJEICAO_APOS_REGISTRO", "ERRO"].includes(item.baixa)) return true;
	if (item.status === "OUTRO") return false;
	if (item.conciliacao === "BOLETO_CANCELADO") return ["REGISTRADO", "LIQUIDADO"].includes(item.status);

	return ["NAO_ENCONTRADO", "SEU_NUMERO_DIVERGENTE"].includes(item.conciliacao);
}

function descreverResultado(item) {
	if (item.conciliacao === "SEU_NUMERO_DIVERGENTE") return { texto: "Seu número não confere", classe: COR.ambar };

	if (item.conciliacao === "NAO_ENCONTRADO") {
		return item.status === "OUTRO"
			? { texto: "Sem ação", classe: COR.cinza }
			: { texto: "Título não encontrado", classe: COR.ambar };
	}

	if (item.conciliacao === "BOLETO_CANCELADO") {
		return ["REGISTRADO", "LIQUIDADO"].includes(item.status)
			? { texto: "Registrado em remessa cancelada", classe: COR.vermelho }
			: { texto: "Boleto cancelado", classe: COR.cinza };
	}

	if (RESULTADO_BAIXA[item.baixa]) {
		const [texto, classe] = RESULTADO_BAIXA[item.baixa];
		return { texto, classe };
	}

	if (item.status === "REGISTRADO") return { texto: "Entrada confirmada", classe: COR.azul };
	if (item.status === "BAIXADO") return { texto: "Baixado sem pagamento", classe: COR.cinza };
	if (item.status === "REJEITADO") return { texto: "Rejeitado", classe: COR.vermelho };
	if (item.status === "OUTRO") return { texto: "Sem ação", classe: COR.cinza };

	return { texto: "-", classe: COR.cinza };
}

export default function MainRetornoBoleto() {
	const [convenios, setConvenios] = useState([]);
	const [idBoletoConfig, setIdBoletoConfig] = useState("");
	const [arquivo, setArquivo] = useState(null);
	const [nomeArquivo, setNomeArquivo] = useState("");
	const [processando, setProcessando] = useState(false);
	const [erro, setErro] = useState("");
	const [resultado, setResultado] = useState(null);

	useEffect(() => {
		(async () => {
			try {
				const response = await reqBoletoConfig.listAll();
				const lista = Array.isArray(response?.data) ? response.data : [];
				setConvenios(lista);

				if (lista.length === 1) {
					setIdBoletoConfig(String(lista[0].id_boleto_config));
				}
			} catch (e) {
				setErro("Não foi possível carregar os convênios de boleto.");
			}
		})();
	}, []);

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setArquivo(file);
			setNomeArquivo(file.name);
			setErro("");
		}
	};

	const handleProcessar = async (e) => {
		e.preventDefault();
		if (!idBoletoConfig) {
			setErro("Selecione um convênio de boleto.");
			return;
		}
		if (!arquivo) {
			setErro("Selecione um arquivo de retorno (.RET ou .TXT).");
			return;
		}

		setProcessando(true);
		setErro("");
		setResultado(null);

		try {
			const reader = new FileReader();
			reader.onload = async (evt) => {
				try {
					const conteudo = evt.target.result;
					const res = await reqBoleto.processarRetorno(
						Number(idBoletoConfig),
						conteudo
					);

					if (Array.isArray(res?.data)) {
						setResultado(res.data);
					} else {
						setErro("Resposta inválida do servidor.");
					}
				} catch (err) {
					setErro(
						err?.response?.data ||
							err?.message ||
							"Erro ao processar o arquivo de retorno."
					);
				} finally {
					setProcessando(false);
				}
			};
			reader.onerror = () => {
				setErro("Falha ao ler o arquivo selecionado.");
				setProcessando(false);
			};
			reader.readAsText(arquivo);
		} catch (err) {
			setErro(err?.message || "Erro inesperado ao processar retorno.");
			setProcessando(false);
		}
	};

	// Totais do resultado
	const baixadas = resultado?.filter((r) => BAIXADAS.includes(r.baixa)) || [];
	const registrados = resultado?.filter((r) => r.status === "REGISTRADO") || [];
	const rejeitados = resultado?.filter((r) => r.status === "REJEITADO") || [];
	const liberadas = rejeitados.filter((r) => r.baixa === "PARCELA_LIBERADA");
	const conferir = resultado?.filter(precisaConferir) || [];
	const somaBaixada = baixadas.reduce((acc, r) => acc + Number(r.valor_pago || 0), 0);
	const somaTarifas = (resultado || []).reduce((acc, r) => acc + Number(r.valor_tarifa || 0), 0);

	return (
		<div className="mt-14 flex-1 flex flex-col overflow-hidden min-h-0 bg-gray-50/50 p-6 gap-6">
			{/* Cabeçalho */}
			<div className="bg-white rounded-2xl p-6 border shadow-sm flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
						<FileSpreadsheet size={28} />
					</div>
					<div>
						<h1 className="text-xl font-bold text-gray-900 tracking-tight">
							Retorno de Boletos
						</h1>
						<p className="text-sm text-gray-500 font-medium mt-0.5">
							Processamento de arquivo CNAB (.RET) enviado pelo banco
						</p>
					</div>
				</div>
			</div>

			{/* Formulário de Seleção e Upload */}
			<div className="bg-white rounded-2xl border p-6 shadow-sm">
				<form onSubmit={handleProcessar} className="flex flex-col md:flex-row items-end gap-4">
					{/* Convênio */}
					<div className="flex-1">
						<label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
							<Landmark size={16} className="text-gray-400" />
							Convênio / Banco
						</label>
						<select
							className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-gray-50/50 text-gray-800 font-semibold focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all text-sm"
							value={idBoletoConfig}
							onChange={(e) => setIdBoletoConfig(e.target.value)}
						>
							<option value="">Selecione um convênio...</option>
							{convenios.map((c) => (
								<option key={c.id_boleto_config} value={c.id_boleto_config}>
									{c.descricao} (Banco {c.banco} - Ag. {c.agencia} / Cc. {c.conta})
								</option>
							))}
						</select>
					</div>

					{/* Arquivo .RET */}
					<div className="flex-1">
						<label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
							<Upload size={16} className="text-gray-400" />
							Arquivo de Retorno (.RET)
						</label>
						<div className="relative">
							<input
								type="file"
								accept=".ret,.txt,.crt"
								onChange={handleFileChange}
								className="hidden"
								id="file-retorno"
							/>
							<label
								htmlFor="file-retorno"
								className="w-full h-11 px-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50 hover:bg-emerald-50/40 hover:border-emerald-300 text-gray-700 font-medium text-sm flex items-center justify-between cursor-pointer transition-all"
							>
								<span className="truncate">
									{nomeArquivo ? nomeArquivo : "Clique para selecionar o arquivo .RET..."}
								</span>
								<FileText size={18} className="text-gray-400 shrink-0 ml-2" />
							</label>
						</div>
					</div>

					{/* Botão Processar */}
					<div>
						<button
							type="submit"
							disabled={processando || !arquivo || !idBoletoConfig}
							className="h-11 px-6 rounded-xl font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-600/20 flex items-center gap-2 transition-all active:scale-95 text-sm"
						>
							{processando ? (
								<>
									<RefreshCw size={18} className="animate-spin" />
									Processando...
								</>
							) : (
								<>
									<Check size={18} />
									Processar Retorno
								</>
							)}
						</button>
					</div>
				</form>

				{erro && (
					<div className="mt-4 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 text-sm font-semibold flex items-center gap-3">
						<AlertCircle size={20} className="shrink-0 text-red-600" />
						<div>{erro}</div>
					</div>
				)}
			</div>

			{/* Resultados do Processamento */}
			{resultado && (
				<div className="flex-1 flex flex-col overflow-hidden min-h-0 bg-white rounded-2xl border shadow-sm p-6 gap-6">
					{/* Cards de Resumo */}
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
						<div className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-100 flex items-center justify-between">
							<div>
								<p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
									Parcelas Baixadas
								</p>
								<p className="text-2xl font-black text-emerald-900 mt-1">
									{baixadas.length}{" "}
									<span className="text-sm font-bold text-emerald-700">parcelas</span>
								</p>
								<p className="text-xs font-semibold text-emerald-700 mt-0.5">
									Total: {libFloat.strToCurrency(somaBaixada)}
								</p>
								{somaTarifas > 0 && (
									<p className="text-xs font-semibold text-emerald-700/80 mt-0.5">
										Tarifas bancárias: {libFloat.strToCurrency(somaTarifas)}
									</p>
								)}
							</div>
							<div className="p-3 bg-emerald-600 text-white rounded-xl shadow-sm">
								<CheckCircle2 size={24} />
							</div>
						</div>

						<div className="p-4 rounded-xl bg-blue-50/60 border border-blue-100 flex items-center justify-between">
							<div>
								<p className="text-xs font-bold text-blue-800 uppercase tracking-wider">
									Entradas Confirmadas
								</p>
								<p className="text-2xl font-black text-blue-900 mt-1">
									{registrados.length}{" "}
									<span className="text-sm font-bold text-blue-700">títulos</span>
								</p>
								<p className="text-xs font-semibold text-blue-700 mt-0.5">
									Confirmados pelo banco
								</p>
							</div>
							<div className="p-3 bg-blue-600 text-white rounded-xl shadow-sm">
								<Check size={24} />
							</div>
						</div>

						<div className="p-4 rounded-xl bg-red-50/60 border border-red-100 flex items-center justify-between">
							<div>
								<p className="text-xs font-bold text-red-800 uppercase tracking-wider">
									Rejeitados
								</p>
								<p className="text-2xl font-black text-red-900 mt-1">
									{rejeitados.length}{" "}
									<span className="text-sm font-bold text-red-700">títulos</span>
								</p>
								<p className="text-xs font-semibold text-red-700 mt-0.5">
									{liberadas.length} liberada(s) para nova remessa
								</p>
							</div>
							<div className="p-3 bg-red-600 text-white rounded-xl shadow-sm">
								<XCircle size={24} />
							</div>
						</div>

						<div className="p-4 rounded-xl bg-amber-50/60 border border-amber-100 flex items-center justify-between">
							<div>
								<p className="text-xs font-bold text-amber-800 uppercase tracking-wider">
									Para Conferir
								</p>
								<p className="text-2xl font-black text-amber-900 mt-1">
									{conferir.length}{" "}
									<span className="text-sm font-bold text-amber-700">títulos</span>
								</p>
								<p className="text-xs font-semibold text-amber-700 mt-0.5">
									Não baixados automaticamente
								</p>
							</div>
							<div className="p-3 bg-amber-500 text-white rounded-xl shadow-sm">
								<AlertTriangle size={24} />
							</div>
						</div>
					</div>

					{/* Tabela de Títulos Lidos */}
					<div className="flex-1 overflow-auto rounded-xl border border-gray-200">
						<table className="w-full text-left border-collapse">
							<thead>
								<tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
									<th className="py-3 px-4">Nosso Número</th>
									<th className="py-3 px-4">Seu Número</th>
									<th className="py-3 px-4">Cliente</th>
									<th className="py-3 px-4">Ocorrência</th>
									<th className="py-3 px-4 text-right">Valor Título</th>
									<th className="py-3 px-4 text-right">Valor Pago</th>
									<th className="py-3 px-4 text-right">Tarifa</th>
									<th className="py-3 px-4">Data Pagto</th>
									<th className="py-3 px-4 text-center">Status</th>
									<th className="py-3 px-4">Resultado</th>
									<th className="py-3 px-4">Motivo / Recusa</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100 text-sm">
								{resultado.length === 0 ? (
									<tr>
										<td colSpan={11} className="py-8 text-center text-gray-400 font-medium">
											Nenhum título encontrado no arquivo de retorno.
										</td>
									</tr>
								) : (
									resultado.map((item, idx) => {
										const resultadoItem = descreverResultado(item);

										return (
											<tr
												key={idx}
												className={`transition-colors ${
													precisaConferir(item) ? "bg-amber-50/60 hover:bg-amber-50" : "hover:bg-gray-50/80"
												}`}
											>
												<td className="py-3 px-4 font-mono font-bold text-gray-900">
													{item.nosso_numero || "-"}
												</td>
												<td className="py-3 px-4 font-mono font-medium text-gray-600">
													{item.seu_numero || item.id_conta_entrada || "-"}
												</td>
												<td className="py-3 px-4 font-semibold text-gray-800">
													{item.cliente || "Não Identificado"}
												</td>
												<td className="py-3 px-4 text-gray-600 font-medium">
													{item.ocorrencia || item.ocorrencia_cod}
												</td>
												<td className="py-3 px-4 text-right font-mono font-semibold text-gray-700">
													{libFloat.strToCurrency(item.valor_titulo)}
												</td>
												<td className="py-3 px-4 text-right font-mono font-bold text-emerald-700">
													{item.valor_pago > 0 ? libFloat.strToCurrency(item.valor_pago) : "-"}
												</td>
												<td className="py-3 px-4 text-right font-mono font-medium text-gray-600">
													{item.valor_tarifa > 0 ? libFloat.strToCurrency(item.valor_tarifa) : "-"}
												</td>
												<td className="py-3 px-4 text-gray-600 font-medium">
													{item.data_liquidacao || "-"}
												</td>
												<td className="py-3 px-4 text-center">
													<span
														className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-extrabold ${
															item.status === "LIQUIDADO"
																? "bg-emerald-100 text-emerald-800"
																: item.status === "REGISTRADO"
																? "bg-blue-100 text-blue-800"
																: item.status === "REJEITADO"
																? "bg-red-100 text-red-800"
																: "bg-gray-100 text-gray-800"
														}`}
													>
														{item.status}
													</span>
												</td>
												<td className="py-3 px-4">
													<span
														title={item.baixa_erro || undefined}
														className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${resultadoItem.classe}`}
													>
														{resultadoItem.texto}
													</span>
												</td>
												<td className="py-3 px-4 text-xs text-red-600 font-medium max-w-xs truncate">
													{item.motivo_recusa || item.baixa_erro || "-"}
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}
		</div>
	);
}
